/**
 * Xero account filter pair: a Set/Missing/Specific dropdown plus a
 * conditional code input that appears when "Specific" is picked. The
 * code input uses market-scoped autocomplete via
 * `/api/admin/xero/account-suggestions?market=&kind=`.
 */
import React, { useEffect, useState } from 'react'
import {
  FormControl, Select, MenuItem, Typography, Box, SelectChangeEvent,
} from '@mui/material'
import { apiClient } from '../../../../services/api'
import { XERO_ACCOUNT_FILTER_OPTIONS } from '../types'
import SearchSelect from '../../inputs/SearchSelect'

export interface XeroAccountSuggestion {
  code: string
  name: string
  type: string
  fp_count: number
}

interface XeroAccountFilterFieldProps {
  label: string
  state: string                // '' | 'set' | 'missing' | 'specific'
  code: string                 // code input (only used when state = 'specific')
  onStateChange: (value: string) => void
  onCodeChange: (value: string) => void
  kind: 'sales' | 'purchase'
  market?: string
  disabled?: boolean
}

const labelStyle = {
  fontWeight: 600,
  color: 'text.secondary',
  fontSize: '0.75rem',
  mb: 0.5,
  display: 'block',
}

const XeroAccountFilterField: React.FC<XeroAccountFilterFieldProps> = ({
  label, state, code, onStateChange, onCodeChange, kind, market, disabled,
}) => {
  const [suggestions, setSuggestions] = useState<XeroAccountSuggestion[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (state !== 'specific') return
    let cancelled = false
    const params = new URLSearchParams({ kind })
    if (market) params.set('market', market)
    setLoading(true)
    apiClient
      .get(`/admin/xero/account-suggestions?${params.toString()}`)
      .then((res) => { if (!cancelled) setSuggestions(res.data?.suggestions || []) })
      .catch((err) => { console.error('Failed to fetch Xero account suggestions:', err) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [state, kind, market])

  const handleStateChange = (e: SelectChangeEvent) => {
    const next = e.target.value
    onStateChange(next)
    if (next !== 'specific') onCodeChange('')
  }

  const selectedSuggestion = suggestions.find((s) => s.code === code) || null

  return (
    <Box>
      <Typography component="label" sx={labelStyle}>{label}</Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 140, flex: state === 'specific' ? '0 0 140px' : 1 }}>
          <Select displayEmpty value={state} onChange={handleStateChange} disabled={disabled}>
            {XERO_ACCOUNT_FILTER_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {state === 'specific' && (
          <SearchSelect<XeroAccountSuggestion>
            sx={{ flex: 1, minWidth: 200 }}
            freeSolo
            options={suggestions}
            loading={loading}
            value={selectedSuggestion}
            inputValue={code}
            disabled={disabled}
            placeholder="e.g. 8000-500"
            getOptionLabel={(o) =>
              typeof o === 'string' ? o : `${o.code} - ${o.name}`
            }
            isOptionEqualToValue={(o, v) => o.code === v.code}
            onInputChange={(v) => onCodeChange(v)}
            onChange={(picked: XeroAccountSuggestion | string | null) => {
              if (!picked) { onCodeChange(''); return }
              if (typeof picked === 'string') { onCodeChange(picked); return }
              onCodeChange(picked.code)
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.code}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.code} - {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.type}  ·  {option.fp_count} FP{option.fp_count === 1 ? '' : 's'}
                  </Typography>
                </Box>
              </li>
            )}
          />
        )}
      </Box>
    </Box>
  )
}

export default XeroAccountFilterField
